import React, { useEffect, useState, useMemo } from "react";
import {
  ShieldCheck, KeyRound, RefreshCw, Copy, Check, ChevronLeft,
  AlertTriangle, X, LogOut,
  MessageCircle, Lock, Image as ImageIcon, Radio, Download,
} from "lucide-react";
import {
  generateMnemonicWords,
  deriveKeypair,
  isValidMnemonic,
  saveIdentity,
  buildBackupBlob,
} from "./lib/identity";
import { fetchPostsByAuthor, generateAiAvatar, upsertProfile, uploadAvatarBlob } from "./lib/postsApi";
import { fetchMyNetwork, fetchProfile } from "./lib/socialApi";
import { devDebug } from "./lib/devLog";

// ============ Registrazione ============
const AVATAR_URLS = [
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=140&h=140&fit=crop&crop=faces",
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=140&h=140&fit=crop&crop=faces",
];

const AVATAR_SIZE = 256;
const AVATAR_INPUT_MAX_BYTES = 5 * 1024 * 1024;
const AVATAR_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

function canvasBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error("Impossibile creare l’avatar")),
      "image/webp",
      0.86,
    );
  });
}

function loadBrowserImage(url) {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Immagine non leggibile"));
    image.src = url;
  });
}

async function resizeAvatarFile(file) {
  if (!AVATAR_TYPES.has(file?.type)) throw new Error("Usa un’immagine JPEG, PNG o WebP");
  if (file.size > AVATAR_INPUT_MAX_BYTES) throw new Error("L’immagine originale non può superare 5 MB");
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadBrowserImage(objectUrl);
    const canvas = document.createElement("canvas");
    canvas.width = AVATAR_SIZE;
    canvas.height = AVATAR_SIZE;
    const ctx = canvas.getContext("2d");
    const side = Math.min(image.naturalWidth, image.naturalHeight);
    const sx = (image.naturalWidth - side) / 2;
    const sy = (image.naturalHeight - side) / 2;
    ctx.drawImage(image, sx, sy, side, side, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
    return { preview: canvas.toDataURL("image/webp", 0.86), blob: await canvasBlob(canvas), source: "upload" };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function generateLocalAvatar(name, gender) {
  const seedText = `${name.trim() || "OpinioneLibera"}-${gender}-${Date.now()}-${Math.random()}`;
  let seed = 0;
  for (let i = 0; i < seedText.length; i += 1) seed = ((seed << 5) - seed + seedText.charCodeAt(i)) | 0;
  const pick = (items, offset = 0) => items[Math.abs(seed + offset) % items.length];
  const canvas = document.createElement("canvas");
  canvas.width = AVATAR_SIZE;
  canvas.height = AVATAR_SIZE;
  const ctx = canvas.getContext("2d");
  const hueA = Math.abs(seed) % 360;
  const hueB = (hueA + 80 + (Math.abs(seed) % 90)) % 360;
  const gradient = ctx.createLinearGradient(0, 0, AVATAR_SIZE, AVATAR_SIZE);
  gradient.addColorStop(0, `hsl(${hueA} 62% 43%)`);
  gradient.addColorStop(1, `hsl(${hueB} 72% 68%)`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);

  const skin = pick(["#f3c7a5", "#d99b73", "#b87550", "#7d4935"], 17);
  const hair = pick(["#201813", "#4a2b1b", "#7b4a27", "#d1a35d", "#4b4b52"], 31);
  const eye = pick(["#2e2119", "#36566e", "#436244", "#6b4a2d"], 47);

  // Capelli posteriori: corti per uomo, più lunghi per donna.
  ctx.fillStyle = hair;
  if (gender === "female") {
    ctx.beginPath();
    ctx.ellipse(128, 122, 76, 102, 0, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.ellipse(128, 79, 67, 43, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // Collo, orecchie e volto.
  ctx.fillStyle = skin;
  ctx.fillRect(108, 171, 40, 55);
  ctx.beginPath();
  ctx.ellipse(65, 126, 13, 20, 0, 0, Math.PI * 2);
  ctx.ellipse(191, 126, 13, 20, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(128, 125, gender === "female" ? 61 : 65, gender === "female" ? 78 : 75, 0, 0, Math.PI * 2);
  ctx.fill();

  // Acconciatura anteriore differenziata.
  ctx.fillStyle = hair;
  ctx.beginPath();
  if (gender === "female") {
    ctx.moveTo(68, 105);
    ctx.quadraticCurveTo(82, 41, 137, 48);
    ctx.quadraticCurveTo(191, 55, 190, 111);
    ctx.quadraticCurveTo(159, 72, 124, 78);
    ctx.quadraticCurveTo(91, 83, 68, 105);
  } else {
    ctx.moveTo(66, 100);
    ctx.quadraticCurveTo(72, 52, 125, 48);
    ctx.quadraticCurveTo(183, 48, 191, 101);
    ctx.quadraticCurveTo(160, 73, 126, 76);
    ctx.quadraticCurveTo(91, 75, 66, 100);
  }
  ctx.fill();

  // Sopracciglia, occhi, naso e bocca.
  ctx.strokeStyle = hair;
  ctx.lineWidth = gender === "female" ? 4 : 5;
  ctx.beginPath();
  ctx.moveTo(88, 112); ctx.quadraticCurveTo(102, 104, 114, 111);
  ctx.moveTo(142, 111); ctx.quadraticCurveTo(155, 104, 169, 112);
  ctx.stroke();
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.ellipse(102, 124, 10, 6, 0, 0, Math.PI * 2);
  ctx.ellipse(154, 124, 10, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = eye;
  ctx.beginPath();
  ctx.arc(102, 124, 4, 0, Math.PI * 2);
  ctx.arc(154, 124, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#9b5f4c";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(128, 128); ctx.lineTo(122, 151); ctx.quadraticCurveTo(128, 155, 135, 151);
  ctx.stroke();
  ctx.strokeStyle = gender === "female" ? "#a83e52" : "#704238";
  ctx.lineWidth = gender === "female" ? 5 : 4;
  ctx.beginPath();
  ctx.moveTo(108, 171); ctx.quadraticCurveTo(128, 183, 149, 170);
  ctx.stroke();

  // Spalle e piccolo monogramma.
  ctx.fillStyle = pick(["#172554", "#3f1d2e", "#153d38", "#3b2f23"], 61);
  ctx.beginPath();
  ctx.ellipse(128, 256, 104, 64, 0, Math.PI, Math.PI * 2);
  ctx.fill();
  const initials = (name.trim() || "OL").split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
  ctx.fillStyle = "rgba(255,255,255,.9)";
  ctx.font = "700 22px Georgia, serif";
  ctx.textAlign = "right";
  ctx.fillText(initials, 238, 238);
  return { preview: canvas.toDataURL("image/webp", 0.86), blob: await canvasBlob(canvas), source: "ai" };
}

function useCopyWords(words) {
  const [copyDone, setCopyDone] = useState(false);
  const flash = () => {
    setCopyDone(true);
    setTimeout(() => setCopyDone(false), 1500);
  };
  const copyWords = () => {
    if (!navigator.clipboard?.writeText) return flash();
    navigator.clipboard.writeText(words.join(" ")).then(flash).catch(flash);
  };
  return { copyDone, copyWords };
}

function isVerifyOk(values, words) {
  return (
    values[3].trim().toLowerCase() === words[3] &&
    values[7].trim().toLowerCase() === words[7] &&
    values[11].trim().toLowerCase() === words[11]
  );
}

function RegistrationSteps({ step, setStep, name, setName, avatarGender, setAvatarGender, avatarSelection, setAvatarSelection, verifyValues, setVerifyValues, words, onComplete, onRecover, recoveryError, recovering, mode }) {
  const { copyDone, copyWords } = useCopyWords(words);
  const verifyOk = isVerifyOk(verifyValues, words);

  if (mode === "recover") {
    return (
      <StepRecover
        onSuccess={onRecover}
        onBack={() => setStep(1)}
        error={recoveryError}
        busy={recovering}
      />
    );
  }
  if (step === 1) return <StepAgeGate onNext={() => setStep(2)} onRecover={() => setStep(0)} />;
  if (step === 2) return (
    <StepIdentity
      name={name} setName={setName}
      avatars={AVATAR_URLS} avatarGender={avatarGender} setAvatarGender={setAvatarGender} avatarSelection={avatarSelection} setAvatarSelection={setAvatarSelection}
      onNext={() => setStep(3)} onBack={() => setStep(1)}
    />
  );
  if (step === 3) return <StepWords words={words} onCopy={copyWords} copyDone={copyDone} onNext={() => setStep(4)} onBack={() => setStep(2)} />;
  if (step === 4) return (
    <StepVerify
      values={verifyValues} setValues={setVerifyValues}
      verifyOk={verifyOk} onNext={() => setStep(5)} onBack={() => setStep(3)}
    />
  );
  return <StepDone name={name} avatar={avatarSelection.preview} onEnter={() => onComplete({ name, avatar: avatarSelection.preview, avatarBlob: avatarSelection.blob, avatarSource: avatarSelection.source, avatarGender, words })} />;
}

export function RegistrationView({ onComplete }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [avatarGender, setAvatarGender] = useState("male");
  const [avatarSelection, setAvatarSelection] = useState({ preview: "", blob: null });
  const [verifyValues, setVerifyValues] = useState({ 3: "", 7: "", 11: "" });
  const [recoveryError, setRecoveryError] = useState(null);
  const [recovering, setRecovering] = useState(false);
  // Real 12-word BIP39 mnemonic generated ONCE per wizard session.
  const words = useMemo(() => generateMnemonicWords(), []);

  const handleRecover = async (inputWords) => {
    setRecoveryError(null);
    if (!isValidMnemonic(inputWords)) {
      setRecoveryError("Mnemonica non valida (checksum errato). Ricontrolla le parole.");
      return;
    }
    setRecovering(true);
    try {
      const keys = await deriveKeypair(inputWords);
      let profile = null;
      try { profile = await fetchProfile(keys.identityId); } catch (err) { /* no profile yet */ devDebug("recover: fetchProfile failed", err?.message); }
      if (!profile?.displayName || !profile?.avatar) {
        setRecoveryError("Nessun profilo completo è associato a queste 12 parole.");
        return;
      }
      const recoveredName = profile.displayName;
      const recoveredAvatar = profile.avatar;
      await saveIdentity({
        name: recoveredName,
        avatar: recoveredAvatar,
        mnemonic: inputWords.join(" "),
        publicKeyHex: keys.publicKeyHex,
        identityId: keys.identityId,
      });
      onComplete({
        name: recoveredName,
        avatar: recoveredAvatar,
        identityId: keys.identityId,
        publicKeyHex: keys.publicKeyHex,
      });
    } catch (err) {
      setRecoveryError(`Ripristino fallito: ${err?.message || "errore sconosciuto"}`);
    } finally {
      setRecovering(false);
    }
  };

  const handleComplete = async (data) => {
    const keys = await deriveKeypair(data.words);
    const identityBase = {
      name: data.name,
      mnemonic: data.words.join(" "),
      publicKeyHex: keys.publicKeyHex,
      identityId: keys.identityId,
    };
    // Save the cryptographic identity first: the protected avatar endpoint
    // needs it to mint and sign the one-shot upload token.
    let finalAvatar = data.avatar;
    await saveIdentity({ ...identityBase, avatar: finalAvatar });
    if (data.avatarSource === "ai") {
      try {
        finalAvatar = await generateAiAvatar(data.avatarGender);
        await saveIdentity({ ...identityBase, avatar: finalAvatar });
      } catch (err) {
        devDebug("AI avatar generation failed; using local fallback", err?.message);
        if (data.avatarBlob) {
          try {
            finalAvatar = await uploadAvatarBlob(data.avatarBlob);
            await saveIdentity({ ...identityBase, avatar: finalAvatar });
          } catch (uploadErr) {
            devDebug("fallback avatar upload failed", uploadErr?.message);
          }
        }
      }
    } else if (data.avatarBlob) {
      try {
        finalAvatar = await uploadAvatarBlob(data.avatarBlob);
        await saveIdentity({ ...identityBase, avatar: finalAvatar });
      } catch (err) {
        devDebug("custom avatar upload failed", err?.message);
      }
    }
    // Register the public profile so the feed shows the real pseudonym.
    // Failure is non-blocking — the app still works with local identity.
    try {
      await upsertProfile({ displayName: data.name, avatar: finalAvatar });
    } catch (err) {
      devDebug("upsertProfile after registration failed", err?.message);
    }
    onComplete({
      name: data.name,
      avatar: finalAvatar,
      identityId: keys.identityId,
      publicKeyHex: keys.publicKeyHex,
    });
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-6" data-testid="registration-view" translate="yes">
      <img
        src="/you-voice-brand.png?v=20260811"
        alt="You Voice"
        className="onboarding-brand"
        data-testid="onboarding-brand"
      />
      <div className="w-full max-w-md card p-6" data-testid={`registration-step-${step}`}>
        <RegistrationSteps
          step={step} setStep={setStep}
          name={name} setName={setName}
          avatarGender={avatarGender} setAvatarGender={setAvatarGender}
          avatarSelection={avatarSelection} setAvatarSelection={setAvatarSelection}
          verifyValues={verifyValues} setVerifyValues={setVerifyValues}
          words={words}
          onComplete={handleComplete}
          onRecover={handleRecover}
          recoveryError={recoveryError}
          recovering={recovering}
          mode={step === 0 ? "recover" : "create"}
        />
      </div>
      <ProgressDots step={step === 0 ? 0 : step} total={5} />
    </div>
  );
}

function ProgressDots({ step, total }) {
  return (
    <div className="flex gap-1.5 mt-6">
      {Array.from({ length: total }).map((_, i) => (
        <div key={`dot-${i}`} className={`w-2 h-2 rounded-full ${i < step ? "bg-primary" : "bg-edge"}`} />
      ))}
    </div>
  );
}

function StepAgeGate({ onNext, onRecover }) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center gap-1.5 border-2 border-primary rounded-full px-4 py-1 text-primary font-bold text-sm mb-4">18+</div>
      <h2 className="font-display font-semibold text-3xl mb-2">Piattaforma per adulti</h2>
      <p className="text-sm text-muted mb-6">Confermando qui sotto dichiari di avere almeno 18 anni e di accettare le regole di moderazione soft.</p>
      <button className="big-cta" onClick={onNext} data-testid="confirm-age-btn">
        Confermo · Ho più di 18 anni
      </button>
      <button
        className="mt-4 text-sm text-primary hover:underline font-semibold"
        onClick={onRecover}
        data-testid="recover-btn"
      >
        Ho già le 12 parole · Ripristina profilo
      </button>
      <p className="text-[11px] text-muted mt-4">Non chiediamo mai email, telefono o dati reali.</p>
    </div>
  );
}

function StepRecover({ onSuccess, onBack, error, busy }) {
  const [inputs, setInputs] = React.useState(Array(12).fill(""));
  const setWord = (i, v) => {
    const clean = v.trim().toLowerCase();
    setInputs((arr) => {
      const copy = arr.slice();
      copy[i] = clean;
      return copy;
    });
  };
  const onPaste = (i, e) => {
    const text = e.clipboardData.getData("text") || "";
    const parts = text.trim().split(/\s+/);
    if (parts.length >= 12) {
      e.preventDefault();
      setInputs(parts.slice(0, 12).map((w) => w.toLowerCase()));
    }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      const j = JSON.parse(text);
      if (typeof j?.mnemonic === "string") {
        const words = j.mnemonic.trim().split(/\s+/);
        if (words.length === 12) setInputs(words.map((w) => w.toLowerCase()));
      }
    } catch (err) { devDebug("recover: backup file parse failed", err?.message); }
  };
  const allFilled = inputs.every((w) => w.length > 0);
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="text-muted hover:text-ink" aria-label="Indietro">
          <ChevronLeft size={18} />
        </button>
        <h2 className="font-display font-semibold text-2xl">Ripristina profilo</h2>
      </div>
      <p className="text-xs text-muted mb-4">
        Inserisci le 12 parole (o incolla la mnemonic completa in una sola casella, o carica il file di backup).
      </p>
      <div className="grid grid-cols-3 gap-2 mb-3" data-testid="recover-grid">
        {inputs.map((w, i) => (
          <div key={`recover-${i}`} className="border border-edge rounded-md px-2 py-1.5 flex items-center gap-1.5 bg-white">
            <span className="text-[10px] text-muted tabular-nums">{i + 1}</span>
            <input
              type="text"
              value={w}
              onChange={(e) => setWord(i, e.target.value)}
              onPaste={(e) => onPaste(i, e)}
              className="w-full text-sm font-mono outline-none"
              data-testid={`recover-word-${i}`}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
            />
          </div>
        ))}
      </div>
      <label className="block text-xs text-muted mb-3 cursor-pointer">
        <span className="underline">Oppure carica il file JSON di backup</span>
        <input type="file" accept="application/json,.json" onChange={onFile} className="hidden" data-testid="recover-file" />
      </label>
      {error && (
        <div className="mb-3 text-xs text-primary flex items-center gap-2" data-testid="recover-error">
          <AlertTriangle size={12} /> {error}
        </div>
      )}
      <button
        className="big-cta"
        onClick={() => onSuccess(inputs)}
        disabled={!allFilled || busy}
        data-testid="recover-submit-btn"
      >
        {busy ? "Ripristino in corso…" : "Ripristina il mio profilo"}
      </button>
    </div>
  );
}

function StepIdentity({ name, setName, avatars, avatarGender, setAvatarGender, avatarSelection, setAvatarSelection, onNext, onBack }) {
  const [avatarError, setAvatarError] = useState("");
  const [avatarBusy, setAvatarBusy] = useState(false);

  const chooseFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setAvatarBusy(true);
    setAvatarError("");
    try {
      setAvatarSelection(await resizeAvatarFile(file));
    } catch (err) {
      setAvatarError(err?.message || "Impossibile caricare l’immagine");
    } finally {
      setAvatarBusy(false);
    }
  };

  const generateAvatar = async () => {
    setAvatarBusy(true);
    setAvatarError("");
    try {
      setAvatarSelection(await generateLocalAvatar(name, avatarGender));
    } catch (err) {
      setAvatarError(err?.message || "Impossibile generare l’avatar");
    } finally {
      setAvatarBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="text-muted hover:text-ink" aria-label="Indietro"><ChevronLeft size={18} /></button>
        <h2 className="font-display font-semibold text-2xl">Crea il tuo profilo</h2>
      </div>
      <p className="text-xs text-muted mb-4">Usa uno pseudonimo. La tua identità reale resta protetta.</p>

      <label className="text-[11px] uppercase tracking-widest text-muted">Scegli come pseudonimo</label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="es. UomoLibero, VoceCritica…"
        className="mt-1 w-full border border-edge rounded-md px-3 py-2 text-sm outline-none focus:border-ink"
        data-testid="pseudonimo-input"
        maxLength={24}
      />
      <p className="text-[11px] text-muted mt-1">Modificabile ogni 7 giorni · Fino a 24 caratteri</p>

      <div className="mt-4">
        <label className="text-[11px] uppercase tracking-widest text-muted">Scegli il tipo di avatar da generare</label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          {[
            { value: "male", label: "Uomo", src: avatars[0] },
            { value: "female", label: "Donna", src: avatars[1] },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => { setAvatarGender(option.value); setAvatarError(""); }}
              className={`flex items-center gap-3 border-2 rounded-lg p-2 text-left ${avatarGender === option.value ? "border-primary bg-primary/5" : "border-edge"}`}
              data-testid={`avatar-gender-${option.value}`}
              aria-pressed={avatarGender === option.value}
            >
              <img src={option.src} alt="" className="w-12 h-12 rounded-full object-cover" />
              <span className="font-semibold">{option.label}</span>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted mt-2">Queste immagini indicano soltanto il tipo uomo/donna: non diventeranno l’avatar del profilo.</p>

        {avatarSelection.blob && (
          <div className="mt-3 flex items-center gap-3 border border-edge rounded-lg p-2">
            <img src={avatarSelection.preview} alt="Anteprima avatar personalizzato" className="w-16 h-16 rounded-full object-cover border-2 border-primary" />
            <span className="text-sm font-semibold">Anteprima del tuo avatar</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 mt-3">
          <label className="brutal-btn ghost justify-center cursor-pointer" data-testid="avatar-upload-label">
            <ImageIcon size={14} /> Carica foto
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={chooseFile}
              className="hidden"
              data-testid="avatar-file-input"
            />
          </label>
          <button
            type="button"
            className="brutal-btn ghost justify-center"
            onClick={generateAvatar}
            disabled={avatarBusy}
            data-testid="avatar-generate-btn"
          >
            <RefreshCw size={14} className={avatarBusy ? "animate-spin" : ""} />
            {avatarBusy ? "Attendi…" : `Genera ${avatarGender === "male" ? "uomo" : "donna"}`}
          </button>
        </div>
        <p className="text-[11px] text-muted mt-2">In alternativa puoi caricare una tua foto. Viene ritagliata e ridimensionata automaticamente; JPEG, PNG o WebP, massimo 5 MB.</p>
        {avatarError && <p className="text-xs text-primary mt-2" role="alert">{avatarError}</p>}
      </div>

      <button className="big-cta mt-6" disabled={name.trim().length < 3 || avatarBusy || !avatarSelection.blob} onClick={onNext} data-testid="identity-next-btn">
        Continua
      </button>
    </div>
  );
}

function StepWords({ words, onCopy, copyDone, onNext, onBack }) {
  const downloadBackup = () => {
    const blob = buildBackupBlob({ mnemonic: words.join(" ") });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "opinione-libera-backup.json";
    a.click();
    URL.revokeObjectURL(url);
  };  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="text-muted hover:text-ink" aria-label="Indietro"><ChevronLeft size={18} /></button>
        <h2 className="font-display font-semibold text-2xl">Le tue 12 parole</h2>
      </div>
      <div className="flex items-start gap-3 p-3 bg-primary/5 border border-primary/30 rounded-md mb-4">
        <ShieldCheck size={20} className="text-primary shrink-0 mt-0.5" />
        <p className="text-xs text-ink">
          Queste 12 parole sono <strong>l'unica via</strong> per recuperare il tuo profilo su altri dispositivi.
          Salvale bene: nessuno, nemmeno noi, può recuperarle al posto tuo.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2" data-testid="bip39-grid" data-no-translate="true" translate="no">
        {words.map((w, i) => (
          <div key={`word-${i}`} className="border border-edge rounded-md px-2 py-1.5 flex items-center gap-1.5 bg-white" data-testid={`bip39-word-${i}`}>
            <span className="text-[10px] text-muted tabular-nums">{i + 1}</span>
            <span className="text-sm font-mono font-semibold">{w}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <button className="brutal-btn ghost flex-1 justify-center" onClick={onCopy} data-testid="copy-words-btn">
          {copyDone ? <><Check size={12} /> Copiato</> : <><Copy size={12} /> Copia parole</>}
        </button>
        <button className="brutal-btn ghost flex-1 justify-center" onClick={downloadBackup} data-testid="download-backup-btn">
          <Download size={12} /> Scarica backup
        </button>
      </div>
      <button className="big-cta mt-5" onClick={onNext} data-testid="words-next-btn">
        Ho salvato le 12 parole
      </button>
    </div>
  );
}

function StepVerify({ values, setValues, verifyOk, onNext, onBack }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="text-muted hover:text-ink" aria-label="Indietro"><ChevronLeft size={18} /></button>
        <h2 className="font-display font-semibold text-2xl">Verifica 3 parole</h2>
      </div>
      <p className="text-xs text-muted mb-4">Inserisci le parole richieste per confermare di averle salvate.</p>
      <div className="space-y-3" data-testid="verify-inputs">
        {[3, 7, 11].map((n) => (
          <div key={`verify-${n}`} className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-muted w-20">Parola #{n + 1}</span>
            <input
              type="text"
              value={values[n]}
              onChange={(e) => setValues({ ...values, [n]: e.target.value })}
              className="flex-1 border border-edge rounded-md px-3 py-2 text-sm outline-none focus:border-ink font-mono"
              placeholder="…"
              data-testid={`verify-word-${n}`}
            />
          </div>
        ))}
      </div>
      <button className="big-cta mt-5" disabled={!verifyOk} onClick={onNext} data-testid="verify-next-btn">
        {verifyOk ? "Verifica corretta — continua" : "Compila tutte le 3 parole"}
      </button>
    </div>
  );
}

function StepDone({ name, avatar, onEnter }) {
  return (
    <div className="text-center">
      <div className="inline-flex w-16 h-16 rounded-full bg-primary/10 items-center justify-center mb-3">
        <Check size={28} className="text-primary" />
      </div>
      <h2 className="font-display font-semibold text-3xl mb-1">Profilo creato</h2>
      <p className="text-sm text-muted mb-5">Benvenuto/a, {name || "amico/a"}.</p>
      <div className="inline-flex items-center gap-2 border border-edge rounded-full px-3 py-1.5">
        <img src={avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
        <span className="text-sm font-semibold">{name || "Pseudonimo"}</span>
      </div>
      <button className="big-cta mt-6" onClick={onEnter} data-testid="enter-app-btn">
        Entra nella Home
      </button>
    </div>
  );
}

// ============ Profilo ============
export function ProfileView({ user, onBack, onOpenMessages, onLogout }) {
  const [posts, setPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let active = true;
    async function loadProfileData() {
      if (!user?.identityId) {
        if (active) { setPosts([]); setAnalytics(null); setLoading(false); }
        return;
      }
      setLoading(true);
      setLoadError("");
      try {
        const [network, authored] = await Promise.all([
          fetchMyNetwork(),
          fetchPostsByAuthor(user.identityId),
        ]);
        if (active) {
          setAnalytics(network?.analytics || null);
          setPosts(Array.isArray(authored) ? authored : []);
        }
      } catch (error) {
        if (active) {
          setPosts([]);
          setAnalytics(null);
          setLoadError(error?.message || "Dati del profilo non disponibili.");
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    void loadProfileData();
    return () => { active = false; };
  }, [user?.identityId]);

  return (
    <div className="min-h-screen bg-cream" data-testid="profile-view">
      <ProfileHeader onBack={onBack} />
      <div className="max-w-3xl mx-auto px-6 py-6">
        <ProfileHeaderCard user={user} />
        <ProfileStats analytics={analytics} loading={loading} />
        <ProfileNavRow onOpenMessages={onOpenMessages} onLogout={onLogout} />
        <h3 className="font-display font-semibold text-2xl mt-8 mb-3">I miei messaggi vocali</h3>
        {loadError && <div className="card p-4 text-sm text-primary">{loadError}</div>}
        {!loadError && loading && <div className="card p-4 text-sm text-muted">Caricamento…</div>}
        {!loadError && !loading && posts.length === 0 && (
          <div className="card p-6 text-center text-sm text-muted" data-testid="profile-posts-empty">
            Non hai ancora pubblicato messaggi vocali.
          </div>
        )}
        <div className="space-y-2">
          {posts.map((post) => (
            <div key={post.id} className="card p-3 flex items-center gap-3" data-testid={`my-track-${post.id}`}>
              <div className="mic-btn" aria-hidden="true"><Radio size={16} /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{post.topicSlug}</div>
                <div className="text-xs text-muted">
                  {new Date(post.publishedAt).toLocaleString("it-IT")} · {Math.floor((post.durationSeconds || 0) / 60)}:{String((post.durationSeconds || 0) % 60).padStart(2, "0")}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProfileHeader({ onBack }) {
  return (
    <div className="border-b border-edge bg-cream sticky top-0 z-30">
      <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest hover:text-primary" data-testid="profile-back-btn">
          <ChevronLeft size={14} /> Home
        </button>
        <span className="font-display font-semibold text-lg">Profilo</span>
        <span className="w-[18px]" aria-hidden="true" />
      </div>
    </div>
  );
}

function ProfileHeaderCard({ user }) {
  return (
    <div className="card p-5 flex items-center gap-4">
      <img src={user.avatar} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-primary" />
      <div className="flex-1 min-w-0">
        <h2 className="font-display text-3xl font-semibold leading-none">{user.name}</h2>
        <div className="text-xs text-muted mt-1">@{user.name.replace(/\s+/g, "").toLowerCase()}</div>
        {user.identityId && (
          <div className="text-[10px] text-muted mt-1 font-mono flex items-center gap-1" data-testid="identity-id">
            <KeyRound size={10} /> ID: {user.identityId}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileStats({ analytics, loading }) {
  const stats = [
    { key: "posts", label: "Post", value: analytics?.posts ?? 0 },
    { key: "follower", label: "Follower", value: analytics?.followers ?? 0 },
    { key: "seguiti", label: "Seguiti", value: analytics?.following ?? 0 },
    { key: "mi-piace", label: "Mi piace", value: analytics?.likesReceived ?? 0 },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4" aria-busy={loading}>
      {stats.map((s) => (
        <div key={s.key} className="card p-3 text-center" data-testid={`stat-${s.key}`}>
          <div className="font-display font-semibold text-2xl">{loading ? "—" : s.value}</div>
          <div className="text-[11px] uppercase tracking-widest text-muted">{s.label}</div>
        </div>
      ))}
    </div>
  );
}

function ProfileNavRow({ onOpenMessages, onLogout }) {
  return (
    <>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button className="card p-3 text-left hover:bg-cream" onClick={onOpenMessages} data-testid="open-messages-btn">
          <MessageCircle size={18} className="text-primary" />
          <div className="text-sm font-semibold mt-1">Messaggi privati</div>
          <div className="text-[11px] text-muted">Apri le conversazioni</div>
        </button>
        <button className="card p-3 text-left hover:bg-cream" onClick={onLogout} data-testid="logout-btn">
          <LogOut size={18} className="text-primary" />
          <div className="text-sm font-semibold mt-1">Cambia identità</div>
          <div className="text-[11px] text-muted">Le 12 parole restano tue</div>
        </button>
      </div>
      {/* Zero-knowledge trust badge: reinforces the app's core promise so it
          becomes visible in the profile — a scelta architetturale visibile
          come value proposition. */}
      <div
        className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 py-1.5 text-[11px] font-medium"
        data-testid="zero-knowledge-badge"
        title="Le tue chiavi private non vengono mai inviate al server. Sei tu l'unico che può accedere ai tuoi messaggi."
      >
        <Lock size={12} strokeWidth={2.5} />
        <span>
          <span className="uppercase tracking-widest text-[10px] font-bold text-emerald-700">Zero-knowledge</span>
          <span className="mx-1.5 text-emerald-700/60">·</span>
          Le tue chiavi non lasciano mai questo dispositivo
        </span>
      </div>
    </>
  );
}

// ============ Moderazione ============
export function ModerationView({ onBack }) {
  return (
    <div className="min-h-screen bg-cream" data-testid="moderation-view">
      <ProfileHeader onBack={onBack} />
      <div className="max-w-3xl mx-auto px-6 py-6">
        <h2 className="font-display font-semibold text-4xl mb-1">Le mie segnalazioni</h2>
        <p className="text-sm text-muted mb-4">Qui compariranno soltanto segnalazioni ed esiti restituiti dal server.</p>
        <div className="card p-6 text-center text-muted text-sm" data-testid="moderation-empty">
          Nessuna segnalazione disponibile.
        </div>
      </div>
    </div>
  );
}
